import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Grid,
  Typography,
  styled,
  Box,
  CircularProgress,
} from "@mui/material";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import CustomTextField from "../../../components/common/Textfield";
import {
  INetworkEntry,
  IAddNetworkingStatisticsModalProps,
} from "../components/types/networking";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  paddingRight: theme.spacing(1),
}));

const defaultProviders = ["Econet", "Netone", "Starlink", "Other"];
const defaultRouters = [
  "Cudy One",
  "Cudy Two",
  "Cudy Three",
  "Muzukuru A",
  "Muzukuru B",
  "Tenda",
  "Other",
];
const connectionTypes = [
  "Ethernet",
  "WiFi",
  "USB 1",
  "USB 2",
  "USB 3",
  "USB 4",
];

const AddNetworkingStatisticsModal: React.FC<
  IAddNetworkingStatisticsModalProps
> = ({ open, onClose, onSave, streamId, refreshNetworkData }) => {
  const [networkData, setNetworkData] = useState<INetworkEntry[]>([]);
  const [providerOptions, setProviderOptions] = useState(defaultProviders);
  const [routerOptions, setRouterOptions] = useState(defaultRouters);
  const [openProviderModal, setOpenProviderModal] = useState(false);
  const [openRouterModal, setOpenRouterModal] = useState(false);
  const [newProvider, setNewProvider] = useState("");
  const [newRouter, setNewRouter] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    if (open) {
      setNetworkData(
        connectionTypes.map((type) => ({
          type,
          serviceProvider: "",
          routerType: "",
          speedMbps: "",
        }))
      );
    }
  }, [open]);

  const handleChange = (
    typeIndex: number,
    field: keyof INetworkEntry,
    value: string
  ) => {
    setNetworkData((prevData) =>
      prevData.map((entry, index) =>
        index === typeIndex ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    const filledData = networkData.filter(
      (entry) => entry.serviceProvider || entry.routerType || entry.speedMbps
    );

    if (filledData.length === 0) {
      toast.info("No networking data entered to save.");
      onClose();
      setIsSaving(false);
      return;
    }

    let allSuccessful = true;
    for (const entry of filledData) {
      try {
        const payload = {
          stream_id: streamId,
          network_type: entry.type,
          service_provider: entry.serviceProvider,
          router_type: entry.routerType,
          speed_mbps: entry.speedMbps ? parseFloat(entry.speedMbps) : null,
        };

        await apiService.post("/api/streams/networking/", payload);
        toast.success(`${entry.type} networking data saved successfully!`);
      } catch (error: unknown) {
        allSuccessful = false;
        const message =
          (error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ||
          (typeof (error as { response?: { data?: unknown } })?.response?.data ===
            "object"
            ? Object.values(
                (error as { response?: { data?: Record<string, unknown> } })
                  ?.response?.data || {}
              )
                .flat()
                .join(" ")
            : (error as { message?: string })?.message);
        toast.error(`Error saving ${entry.type} networking data: ${message}`);
      }
    }

    setIsSaving(false);

    if (allSuccessful) {
      if (onSave) onSave(filledData);
      if (refreshNetworkData) await refreshNetworkData();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitleStyled>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Add Networking Statistics
          </Typography>
        </Box>
      </DialogTitleStyled>

      <DialogContent>
        <Grid container spacing={3}>
          {networkData.map((entry, index) => (
            <React.Fragment key={entry.type}>
              <Grid size={{ xs: 12, sm: 2 }} display="flex" alignItems="center">
                <Typography variant="body1" fontWeight="bold">
                  {entry.type}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 10 }}>
                <Grid container spacing={2}>
                  {/* Service Provider */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    {" "}
                    <CustomTextField
                      select
                      fullWidth
                      label="Service provider"
                      value={entry.serviceProvider}
                      onChange={(e) =>
                        handleChange(index, "serviceProvider", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                    >
                      {providerOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {/* Router Type */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <CustomTextField
                      select
                      fullWidth
                      label="Router type"
                      value={entry.routerType}
                      onChange={(e) =>
                        handleChange(index, "routerType", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                    >
                      {routerOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>

                  {/* Speed (mbps) */}
                  <Grid size={{ xs: 4 }}>
                    <CustomTextField
                      fullWidth
                      label="Speed (mbps)"
                      type="number"
                      value={entry.speedMbps}
                      onChange={(e) =>
                        handleChange(index, "speedMbps", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      inputProps={{ min: "0" }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, display: "flex", justifyContent: "space-between" }}
      >
        {/* Left side buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<AddCircleOutlineOutlined />}
            variant="contained"
            sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
            onClick={() => setOpenProviderModal(true)}
            disabled={isSaving}
          >
            Provider
          </Button>
          <Button
            startIcon={<AddCircleOutlineOutlined />}
            variant="contained"
            sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
            onClick={() => setOpenRouterModal(true)}
            disabled={isSaving}
          >
            Router
          </Button>
        </Box>
        {/* Right side buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </DialogActions>

      {/* Add Provider Dialog */}
      <Dialog
        open={openProviderModal}
        onClose={() => setOpenProviderModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add New Provider</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            label="Provider Name"
            value={newProvider}
            onChange={(e) => setNewProvider(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProviderModal(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (newProvider && !providerOptions.includes(newProvider)) {
                setProviderOptions([...providerOptions, newProvider]);
              }
              setNewProvider("");
              setOpenProviderModal(false);
            }}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Router Dialog */}
      <Dialog
        open={openRouterModal}
        onClose={() => setOpenRouterModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add New Router</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            label="Router Type"
            value={newRouter}
            onChange={(e) => setNewRouter(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRouterModal(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (newRouter && !routerOptions.includes(newRouter)) {
                setRouterOptions([...routerOptions, newRouter]);
              }
              setNewRouter("");
              setOpenRouterModal(false);
            }}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddNetworkingStatisticsModal;
